import { createContext, type PropsWithChildren, useContext } from 'react';
import { type FooterV2Response } from '@cx-web/api-clients/footer-nav-api/types';
import { type NavV2Response } from '@cx-web/api-clients/nav-api/types';

import { type DeviceType, type Repub } from '@cx-modules/types';

export interface LayoutState {
  deviceType: DeviceType;
  footerData: FooterV2Response;
  isAdmiralCookieBannerEnabled: boolean;
  navData: NavV2Response;
  repub: Repub;
  resourcesUrl: string;
}

const LayoutContext = createContext<LayoutState | null>(null);
LayoutContext.displayName = 'LayoutStore';

export const LayoutStoreProvider = ({ children, value }: PropsWithChildren<{ value: LayoutState }>) => {
  return <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>;
};

// The `selector` parameter is destructuring sugar, not Zustand-style slice memoization.
// React Context propagates whole-value changes, so any consumer re-renders whenever the
// provided value's reference changes — regardless of which slice the selector returns.
// Re-renders are bounded by memoizing `value` at the provider call sites; consumers
// re-render only when the underlying loader data changes.
export function useLayoutStore<T>(selector: (state: LayoutState) => T): T {
  const ctx = useContext(LayoutContext);
  if (!ctx) {
    throw new Error('useLayoutStore must be used within LayoutStoreProvider');
  }
  return selector(ctx);
}
